/**
 * Shared types and helpers for the Invoice Version History feature.
 */

export interface RawInvoiceSnapshot {
  invoice_number?: string
  invoice_date?: string
  due_date?: string
  invoice_type?: string
  supply_type?: string
  status?: string
  party_name?: string
  party_gstin?: string
  party_company_vendor_code?: string
  party_address_line1?: string
  party_address_line2?: string
  party_city?: string
  party_state?: string
  party_state_code?: string
  party_postal_code?: string
  party_country?: string
  party_country_code?: string
  seller_name?: string
  seller_gstin?: string
  seller_pan?: string
  seller_address_line1?: string
  seller_address_line2?: string
  seller_city?: string
  seller_state?: string
  seller_state_code?: string
  seller_postal_code?: string
  seller_country?: string
  seller_contact_email?: string
  seller_contact_phone?: string
  seller_logo_url?: string
  place_of_supply?: string
  currency?: string
  exchange_rate?: string | number
  subtotal?: number
  discount_amount?: number
  taxable_amount?: number
  cgst_amount?: number
  sgst_amount?: number
  igst_amount?: number
  cess_amount?: number
  total_tax?: number
  round_off_amount?: number
  total_amount?: number
  payment_terms?: string
  po_number?: string
  notes?: string
  terms_conditions?: string
  bank_details?: {
    bank_name?: string
    account_name?: string
    account_number?: string
    ifsc?: string
    branch?: string
    swift_code?: string
    iban?: string
    routing_number?: string
    sort_code?: string
  }
  ship_to_address_line1?: string
  ship_to_address_line2?: string
  ship_to_city?: string
  ship_to_state?: string
  ship_to_postal_code?: string
  ship_to_country?: string
  ship_to_gstin?: string
}

export interface RawItemSnapshot {
  line_number?: number
  item_code?: string
  description?: string
  hsn_code?: string
  sac_code?: string
  quantity?: number
  unit?: string
  unit_price?: number
  discount_percent?: number
  cgst_rate?: number
  sgst_rate?: number
  igst_rate?: number
  cess_rate?: number
  cgst_amount?: number
  sgst_amount?: number
  igst_amount?: number
  total_tax?: number
  total_amount?: number
}

export interface RawVersion {
  id: string
  invoiceId: string
  versionNumber: number
  invoiceSnapshot: RawInvoiceSnapshot
  itemsSnapshot: RawItemSnapshot[]
  createdAt: string
  createdBy: string
  changeNotes?: string
}

// ── Helpers ──────────────────────────────────────────────────────

export function isDiff(current: unknown, prev: unknown): boolean {
  if (prev === undefined || prev === null || prev === '') { return false }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- TODO(refactor): review and fix properly
  return String(current ?? '') !== String(prev ?? '')
}

export function str(val: unknown): string {
  if (val === null || val === undefined || val === '') { return '—' }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- TODO(refactor): review and fix properly
  return String(val)
}

export function formatSnapDate(dateStr?: string): string {
  if (!dateStr) { return '—' }
  const d = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`)
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function getSupplyTypeLabel(st?: string): string {
  if (!st) { return '—' }
  const s = st.toLowerCase()
  if (s.includes('intrastate')) { return 'Intrastate (CGST + SGST)' }
  if (s.includes('interstate')) { return 'Interstate (IGST)' }
  if (s.includes('export')) { return 'Export (Zero-rated)' }
  if (s.includes('sez')) { return 'SEZ Supply' }
  return st
}

export function formatAddress(parts: (string | undefined)[]): string {
  return parts.filter(p => p?.trim()).join(', ')
}

export function parseDate(s: string): Date {
  return new Date(s.includes('T') ? s : `${s}T00:00:00`)
}

export function formatVersionDate(dateString: string): string {
  return parseDate(dateString).toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export function formatShortDate(dateString: string): string {
  return parseDate(dateString).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Returns amber left-border + bg tint when field changed */
export function diffWrap(shouldHighlight: boolean, current: unknown, prev: unknown): string {
  return shouldHighlight && isDiff(current, prev)
    ? 'border-l-[3px] border-amber-400 dark:border-amber-500 pl-2 bg-amber-50 dark:bg-amber-950/20 rounded-r-sm'
    : ''
}

/** Returns amber text + weight when field changed */
export function changedClass(shouldHighlight: boolean, current: unknown, prev: unknown): string {
  return shouldHighlight && isDiff(current, prev)
    ? 'text-amber-700 dark:text-amber-300 font-semibold'
    : 'text-foreground'
}
